import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Divider,
  FormControl,
  Grid,
  TextField
} from '@mui/material';
import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import CancelIcon from '@mui/icons-material/Cancel';
import Rating from '@mui/material/Rating';
import CircleIcon from '@mui/icons-material/Circle';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import {
  addNewSkill,
  addSkillsUser,
  getAllSkills
} from 'src/services/employeeService';
import ConfirmationModal from 'src/content/common/confirmationModal/ConfirmationModal';
import { useAppSelector } from 'src/hooks/hooks';
import { AppActions } from 'src/store/AppReducer';
import { useDispatch } from 'react-redux';

const filter = createFilterOptions<any>();

const StyledRating = styled(Rating)({
  '& .MuiRating-iconFilled': {
    color: '#1bc47d'
  },
  '& .MuiRating-iconHover': {
    color: '#1bc47d'
  }
});

const TextFieldStyle = {
  paddingTop: 2,
  paddingLeft: 15,
  paddingRight: 15
};

const LabelStyle = {
  paddingLeft: 15,
  paddingRight: 15,
  fontSize: '14px',
  fontFamily:
    'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
};

const DialogWrapper = styled(Dialog)(
  () => `
          .MuiDialog-container {
              height: 100vh;
          }
          
          .MuiDialog-paperScrollPaper {
              max-height: calc(100vh - 80px)
          }
      `
);

const SkillsAddModal = ({
  toggle,
  employeeId,
  open,
  getSkillData,
  skillsSet
}) => {
  const [value, setValue] = useState<any>(null);
  const [allSkills, setAllSkills] = useState([]);
  const [allSkillsArr, setAllSkillsArr] = useState([]);
  const [addedSkills, setAddedSkills] = useState([]);
  const [rating, setRating] = useState(1);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const toggleConfirmation = () => {
    setOpenConfirmation(!openConfirmation);
  };

  const setAllStateBack = () => {
    setValue(null);
    setRating(1);
    setAddedSkills([]);
    setAllSkillsArr([]);
  };

  const dispatch = useDispatch();

  const getAllListData = () => {
    getAllSkills().then((res) => {
      setAllSkillsArr(res?.data);
      setAllSkills(
        res?.data.map((ele) => {
          return { title: ele };
        })
      );
    });
  };

  const addAllSkills = () => {
    if (addedSkills.length > 0) {
      let allSkillsFiltered = [];
      allSkillsFiltered = addedSkills.map((ad) => {
        return {
          name: ad.name,
          level: ad.level,
          employeeId: employeeId
        };
      });

      addSkillsUser(allSkillsFiltered)
        .then((res) => {
          getSkillData();
          setAllStateBack();
          dispatch(
            AppActions.toggleSnackBarOpen({
              message: 'Skill added successfully.',
              severity: 'success'
            })
          );
          toggle();
        })
        .catch((e) => {
          dispatch(
            AppActions.toggleSnackBarOpen({
              message: 'Unable to add skill.',
              severity: 'error'
            })
          );
        });
    }
  };

  const addSkillToList = () => {
    addNewSkill({ name: value.title }).then((res) => {
      getAllListData();
    });
  };

  useEffect(() => {
    getAllListData();
  }, []);

  const [duplicateError, setDuplicateError] = useState('');

  const addSkill = () => {
    if (allSkillsArr.includes(value.title)) {
      if (
        !addedSkills.some((skill) => skill.name === value.title) &&
        !skillsSet.some((skill) => skill.skill === value.title)
      ) {
        setDuplicateError('');
        setAddedSkills([
          ...addedSkills,
          { name: value.title, level: rating, id: addedSkills.length + 1 }
        ]);
        setValue(null);
        setRating(1);
      } else {
        setDuplicateError('Skill Already Exist');
      }
    } else {
      toggleConfirmation();
    }
  };

  const { isLoading } = useAppSelector((state) => state.appReducer);

  return (
    <DialogWrapper
      open={open}
      keepMounted
      maxWidth="md"
      fullWidth
      scroll="paper"
      onClose={toggle}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ ml: 2, mt: 5 }}>
          <Typography sx={{ fontWeight: 'bold' }} variant="h6">
            Skills
          </Typography>
        </Grid>
        <Divider />
        <Grid display="flex" item xs={12}>
          <Grid item xs={6}>
            <Typography fontWeight="light" style={LabelStyle} variant="h5">
              Skill
            </Typography>
            <FormControl fullWidth>
              <Autocomplete
                fullWidth
                style={TextFieldStyle}
                value={value}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    setValue({
                      title: newValue
                    });
                  } else if (newValue && newValue.inputValue) {
                    // Create a new value from the user input
                    setValue({
                      title: newValue.inputValue
                    });
                  } else {
                    setValue(newValue);
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  const { inputValue } = params;
                  // Suggest the creation of a new value
                  const isExisting = options.some(
                    (option) => inputValue === option.title
                  );
                  if (
                    inputValue !== '' &&
                    !isExisting &&
                    inputValue.trim().length > 0
                  ) {
                    filtered.push({
                      inputValue,
                      title: `Add "${inputValue}"`
                    });
                  }

                  return filtered;
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                id="free-solo-with-text-demo"
                options={allSkills}
                getOptionLabel={(option) => {
                  // Value selected with enter, right from the input
                  if (typeof option === 'string') {
                    return option;
                  }
                  // Add "xxx" option created dynamically
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  // Regular option
                  return option.title;
                }}
                renderOption={(props, option) => (
                  <li {...props}>{option.title}</li>
                )}
                freeSolo
                renderInput={(params) => (
                  <TextField {...params} placeholder="Enter Skill" />
                )}
                size="small"
              />
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <Typography fontWeight="light" style={LabelStyle} variant="h5">
              Level
            </Typography>
            <StyledRating
              id="btn-rating"
              sx={{ pt: '0.65rem' }}
              name="customized-color"
              defaultValue={rating}
              value={rating}
              onChange={(event, newValue) => {
                setRating(newValue);
              }}
              precision={1}
              icon={<CircleIcon fontSize="small" />}
              emptyIcon={<CircleIcon fontSize="small" />}
            />
          </Grid>
          <Grid
            display="flex"
            flexDirection="column"
            justifyContent="end"
            item
            xs={2}
          >
            <Button
              id="btn-addSkill"
              disabled={!value}
              type="button"
              onClick={() => addSkill()}
              sx={{ width: '40%', mt: '1.25rem', mr: 3, ml: 'auto' }}
              variant="contained"
            >
              Add
            </Button>
            {duplicateError && (
              <Typography
                style={{ marginLeft: '0.75rem' }}
                sx={{ color: 'red' }}
                variant="body2"
              >
                {duplicateError}
              </Typography>
            )}
          </Grid>
        </Grid>

        {addedSkills?.length > 0 &&
          addedSkills.map((skill) => (
            <Grid display="flex" alignItems="center" item xs={12}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    id="skillName"
                    disabled
                    size="small"
                    style={TextFieldStyle}
                    fullWidth
                    variant="outlined"
                    value={skill.name}
                    aria-readonly
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <StyledRating
                  id="skillLevel"
                  sx={{ pt: '0.65rem' }}
                  name="customized-color"
                  value={skill.level}
                  disabled
                  precision={1}
                  icon={<CircleIcon fontSize="small" />}
                  emptyIcon={<CircleIcon fontSize="small" />}
                />
              </Grid>
              <Grid display="flex" justifyContent="end" item xs={2}>
                <CancelIcon
                  id="btn-setAddedSkills"
                  sx={{ mr: 3, cursor: 'pointer' }}
                  fontSize="small"
                  color="action"
                  onClick={() =>
                    setAddedSkills(
                      addedSkills.filter((ad) => ad.id !== skill.id)
                    )
                  }
                />
              </Grid>
            </Grid>
          ))}

        <Grid item xs={6}></Grid>
        <Grid
          item
          sx={{
            mb: 2,
            mt: 2,
            pr: 2,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
          xs={6}
        >
          <Button
            id="btn-setAllStateBack"
            sx={{ width: '30%' }}
            variant="outlined"
            onClick={() => {
              setAllStateBack();
              toggle();
            }}
          >
            Cancel
          </Button>
          <Button
            id="btn-addAllSkills"
            onClick={() => addAllSkills()}
            type="button"
            sx={{ ml: 2, width: '30%' }}
            variant="contained"
          >
            Add
          </Button>
        </Grid>
        <ConfirmationModal
          isLoading={isLoading}
          open={openConfirmation}
          toggle={toggleConfirmation}
          message={`Are you sure you want to add new skill named "${value?.title}"?`}
          onConfirm={() => {
            setAddedSkills([
              ...addedSkills,
              { name: value.title, level: rating }
            ]);
            addSkillToList();
            toggleConfirmation();
          }}
          noToggle={toggleConfirmation}
        />
      </Grid>
    </DialogWrapper>
  );
};

export default SkillsAddModal;
